/* Mini-PDP duplicate-id scoping fix.

   When the same product is rendered as a card on multiple surfaces (search
   drawer Best Sellers + predictive-search results, home page collection grid +
   search drawer, recently-viewed strip + Best Sellers, etc.) and at least one
   placeholder quick-shop has been expanded — by an explicit click OR by the
   silent mouseenter prefetch in QuickShop._setUpFetchListeners — the page ends
   up with TWO <quick-shop> elements containing the same product DOM.

   Both expanded quick-shops carry the same data-card="quick-shop--{product_id}".
   That id propagates into VariantSelects/VariantMixedInputs.uniqueId and from
   there into Liquid-rendered ids:
       price-quick-shop--{id}
       unit-price-quick-shop--{id}
       label-quick-shop--{id}
       inventory-notice-quick-shop--{id}
       quantity-quick-shop--{id}-{variantId}
       sku-quick-shop--{id}
       product-form-quick-shop--{id}

   VariantSelects.renderProductInfo / toggleAddButton / setUnavailable look
   these up via `document.getElementById(...)`, which always returns the FIRST
   element in tree order. When the popup's quick-shop is later in the tree than
   the duplicate-on-another-card, the lookups all return elements OUTSIDE the
   visible mini-PDP modal. The user clicks a variant, the data does change
   (variants:change fires, hidden input updates), but the visible price /
   buttons / Shop Pay never update — they live in the wrong element.

   Symptoms reported by merchants:
     - "I switch flavor in the mini-PDP from the search bar, nothing changes."
     - "Buy with Shop button is disabled."
     - "Doesn't always happen — fresh window works fine, after a while it breaks."

   Fix: wrap the three VariantSelects.prototype methods that do these lookups.
   Each wrapper temporarily monkey-patches document.getElementById so any lookup
   for an id containing the variant component's own uniqueId is first resolved
   against the variant's nearest <quick-shop> ancestor (or <section> ancestor on
   the real PDP). Falls through to the original document.getElementById for ids
   that don't carry this uniqueId (other extensions / Okendo / etc. are
   unaffected) and for elements that don't live inside a quick-shop (the real
   PDP keeps its existing behaviour, since there is only one of itself).

   No changes to theme.min.js / theme.js / Liquid templates — this file is
   loaded after theme.min.js by layout/theme.liquid and patches the prototypes
   at runtime once the custom elements have registered. */

(function () {
  if (window.__qsIdScopeFixInstalled) return;
  window.__qsIdScopeFixInstalled = true;

  function install() {
    var ctor = window.customElements && window.customElements.get('variant-selects');
    if (!ctor || !ctor.prototype) return false;
    var proto = ctor.prototype;
    if (proto.__qsScopeFixPatched) return true;
    proto.__qsScopeFixPatched = true;

    /* Wrap a method so any document.getElementById(id) call inside it that
       references this instance's uniqueId is first resolved against the local
       <quick-shop> ancestor. Outside <quick-shop> (real PDP), behaviour is
       unchanged because there are no duplicates and the scoped lookup falls
       through to document.getElementById. */
    function wrap(methodName) {
      var orig = proto[methodName];
      if (typeof orig !== 'function') return;
      proto[methodName] = function () {
        var uniqueId = this.uniqueId;
        var scope = this.closest && this.closest('quick-shop');
        if (!scope || !uniqueId) return orig.apply(this, arguments);

        var realGet = Document.prototype.getElementById;
        var unique = String(uniqueId);
        document.getElementById = function (id) {
          /* Scope only ids that carry this variant instance's uniqueId — the
             ones Liquid renders relative to data-card. Other lookups
             (Okendo widgets, predictive-search ids, etc.) bypass entirely. */
          if (id && id.indexOf(unique) !== -1) {
            var found = scope.querySelector('#' + CSS.escape(id));
            if (found) return found;
          }
          return realGet.call(document, id);
        };
        try {
          return orig.apply(this, arguments);
        } finally {
          /* Restore the prototype method by deleting the own-property
             shadow. Safer than reassigning to realGet because it doesn't
             leave a permanent own-property on document. */
          delete document.getElementById;
        }
      };
    }

    /* The three methods whose document.getElementById lookups are
       collision-prone in mini-PDP. See top-of-file comment for the id list. */
    wrap('renderProductInfo');
    wrap('toggleAddButton');
    wrap('setUnavailable');

    return true;
  }

  /* Belt to the prototype-wrap suspenders: the variant-trigger label (the
     dropdown button text that shows the selected flavor) is updated directly
     by quick-shop-variant-sheet.js via `wrapper3 = sheet.closest('.product-
     form__variant') || sheet.__qsOriginalParent` — a different code path
     from VariantSelects. When the sheet was portaled to <body> on mobile
     during an earlier interaction with a DIFFERENT card for the same product,
     __qsOriginalParent can point to that other card's wrapper, and the label
     update visibly never lands on the popup.

     Listen for the variants:change event AFTER VariantSelects has dispatched
     it (capture: false, default order — runs after the component's own
     listener). For variant components inside a <quick-shop>, locate the
     matching option (by variant id) inside the SAME quick-shop and force the
     local trigger label + is-selected class to the right values. This is a
     no-op if the existing code already landed correctly. */
  function syncVisibleTriggerOnVariantChange(event) {
    var vmi = event.target;
    if (!vmi || !vmi.closest) return;
    var qs = vmi.closest('quick-shop');
    if (!qs) return;
    var variant = event.detail && event.detail.variant;
    if (!variant) return;
    var wrapper = qs.querySelector('.product-form__variant');
    if (!wrapper) return;
    var match = wrapper.querySelector(
      '[data-qs-variant-option][data-variant-id="' + variant.id + '"]'
    );
    if (!match) return;
    /* Mark is-selected / aria-selected on options local to this quick-shop. */
    wrapper.querySelectorAll('[data-qs-variant-option]').forEach(function (o) {
      var on = o === match;
      o.classList.toggle('is-selected', on);
      o.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    /* Update the visible trigger label. */
    var label = match.getAttribute('data-variant-title') || variant.title || '';
    var triggerLabel = wrapper.querySelector('[data-qs-variant-label]');
    if (triggerLabel && label) triggerLabel.textContent = label;
  }
  /* `variants:change` is dispatched on the variant component itself and
     bubbles, so a single document-level listener catches every fire across
     every mini-PDP. */
  document.addEventListener('variants:change', syncVisibleTriggerOnVariantChange);

  /* Fix C — variant-option click → drive the visible mini-PDP directly.

     Two failure modes that fix A/B alone don't catch:

     1. `quick-shop-variant-sheet.js` resolves its wrapper via
        `wrapper3 = sheet.__qsOriginalParent || sheet.closest('.product-form__
        variant')`. On mobile/portaled sheets, `__qsOriginalParent` can point
        to an OFF-SCREEN duplicate card's wrapper from an earlier interaction
        on the same product. The change event then fires on the off-screen
        <select>, the off-screen component handles it, fix A correctly scopes
        to the off-screen quick-shop, and the visible popup never updates.

     2. The quick-shop modal initialises imperfectly: SRA fetch → _updateContent
        sets innerHTML → variant-mixed-inputs constructed → connectedCallback
        runs `getVariantData()` which queries `[type="application/json"]`. If
        that lookup throws for any reason (race in custom-element upgrade,
        unexpected SRA response shape, etc.), the `change` listener is never
        attached and the dispatch chain below is dead. No console error, just
        a frozen mini-PDP — exactly the merchant-reported "the pop-up is
        stuck, doesn't let me pick a flavor" symptom.

     Defence: in capture phase, intercept the option click, find the
     CURRENTLY VISIBLE quick-shop (the one inside `.mfp-content`), look up
     the variant in its own JSON, and update the visible DOM directly. We
     also nudge the underlying selects + dispatch a change so any working
     downstream listeners (analytics, Shop Pay, etc.) still fire — but we
     don't depend on them landing for the visible UI to update.

     We don't `stopPropagation` — the existing bubble-phase handler still
     runs and takes care of the sheet open/close UI. */
  function redirectVariantOptionClickToVisibleQs(event) {
    var option = event.target && event.target.closest && event.target.closest('[data-qs-variant-option]');
    if (!option) return;
    var variantId = option.getAttribute('data-variant-id');
    if (!variantId) return;

    /* "Visible quick-shop" = the one inside the open MFP popup. If no popup
       is open, fall back to the quick-shop the option lives in (inline
       on-page quick-shop, real PDP usage doesn't go through this code). */
    var visibleQs = document.querySelector('.mfp-content quick-shop');
    if (!visibleQs) visibleQs = option.closest('quick-shop');
    if (!visibleQs) return;

    /* Read the variant graph straight from the JSON script — don't rely on
       the custom element's connectedCallback having finished successfully.
       This is what makes fix C robust against init failures. */
    var jsonScript = visibleQs.querySelector('variant-mixed-inputs [type="application/json"], variant-selects [type="application/json"], variant-radios [type="application/json"]');
    if (!jsonScript) return;
    var data;
    try { data = JSON.parse(jsonScript.textContent); } catch (e) { return; }
    if (!Array.isArray(data)) return;
    var variant = data.find(function (v) { return String(v.id) === String(variantId); });
    if (!variant) return;

    var wrapper = visibleQs.querySelector('.product-form__variant');

    /* 1. Hidden form input (drives `add` and Buy-with-Shop submits). */
    var hiddenIdInput = visibleQs.querySelector('input[name="id"]');
    if (hiddenIdInput) {
      hiddenIdInput.value = variant.id;
      hiddenIdInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* 2. Underlying <select> values, with a change dispatch so any properly-
          initialised variant component, analytics listener, or 3rd-party app
          on the visible side still runs. */
    var selects = visibleQs.querySelectorAll('variant-mixed-inputs select, variant-selects select, variant-radios select');
    for (var i = 0; i < selects.length; i++) {
      var optionValue = variant.options && variant.options[i];
      if (optionValue == null) continue;
      if (selects[i].value === optionValue) continue;
      selects[i].value = optionValue;
      selects[i].dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* 3. is-selected / aria-selected on the visible option list (so the
          dropdown reflects the new choice next time it opens). */
    if (wrapper) {
      wrapper.querySelectorAll('[data-qs-variant-option]').forEach(function (o) {
        var on = o.getAttribute('data-variant-id') === String(variant.id);
        o.classList.toggle('is-selected', on);
        o.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      /* 4. Trigger label (the dropdown button text). */
      var triggerLabel = wrapper.querySelector('[data-qs-variant-label]');
      if (triggerLabel) {
        var label = option.getAttribute('data-variant-title') || variant.title || '';
        if (label) triggerLabel.textContent = label;
      }
    }

    /* 5. Price element — direct update from variant data so the user sees
          the new price even if the component's renderProductInfo path is
          broken. Mirrors the Liquid markup. */
    var uniqueId = (visibleQs.querySelector('variant-mixed-inputs, variant-selects, variant-radios') || {}).uniqueId;
    if (!uniqueId) {
      var vmiEl = visibleQs.querySelector('variant-mixed-inputs, variant-selects, variant-radios');
      uniqueId = vmiEl && (vmiEl.dataset.card || vmiEl.dataset.section);
    }
    var priceEl = uniqueId ? visibleQs.querySelector('#' + CSS.escape('price-' + uniqueId)) : visibleQs.querySelector('[id^="price-"]');
    if (priceEl && typeof variant.price === 'number' && window.Shopify && window.Shopify.formatMoney) {
      var moneyFormat = (window.theme && window.theme.money_format) || '${{amount}}';
      var isSale = typeof variant.compare_at_price === 'number' && variant.compare_at_price > variant.price;
      var priceHtml =
        '<span class="u-hidden-visually">Regular price</span>' +
        '<span class="price__number' + (isSale ? ' price__number--sale' : '') + '">' +
        '<span class="money">' + window.Shopify.formatMoney(variant.price, moneyFormat) + '</span></span>';
      if (isSale && priceEl.dataset.priceNote !== 'true') {
        priceHtml +=
          '<span class="u-hidden-visually">Sale price</span>' +
          '<s class="price__compare"><span class="money">' +
          window.Shopify.formatMoney(variant.compare_at_price, moneyFormat) +
          '</span></s>';
      }
      priceEl.innerHTML = priceHtml;
    }
  }
  document.addEventListener('click', redirectVariantOptionClickToVisibleQs, true);

  /* Fix D — restore the lost <form> wrapper inside <product-form>.

     Symptom on live (Alani Nu mini-PDP opened from search drawer):
       Buy with Shop greyed (button[disabled] inside shopify-accelerated-
         checkout)
       Add to Cart click does nothing
       Variant change updates the visible label but the form ignores it

     The root cause is in the rendered HTML — there is NO <form> element
     inside <product-form>. The Liquid `{% form 'product', current_product %}`
     normally renders `<form action="/cart/add" method="post">…</form>`,
     but in the broken state the <form> open and close tags are absent
     while the form_type/utf8 hidden inputs (which Shopify's {% form %} tag
     auto-injects INSIDE the <form>) still appear directly inside
     <product-form>. That proves the parser dropped the <form> tag while
     keeping its descendants.

     HTML5 parsing rules: when the parser encounters a `<form>` start tag
     while there is already an open form on the stack (the form element
     pointer is non-null), the new start tag is IGNORED and the inner
     content becomes a child of the surrounding form element instead.

     The search drawer wraps everything in
         <form class="search__form"> … <quick-shop> … </form>
     so the cards and their <quick-shop> placeholders live inside that
     form. In the close-then-reopen MFP dance (search-draw close →
     `closed.cartDraw` → retry show() → quick-shop popup open), there is
     a small window where the SRA fetch resolves and `_updateContent`
     calls
         this.querySelector('.quick-shop__wrapper').innerHTML = newContent…
     while the source <quick-shop> is STILL nested inside the search form
     (because magnific is in the middle of moving DOM around). The parser
     sees nested <form> in the response HTML and drops it. The rendered
     <product-form> ends up with no <form> child.

     `assets/product-form.js`'s `connectedCallback` then does:
         this.form = this.querySelector('form');
         if (!this.form) return;
     and silently bails — so `removeAttribute('disabled')` on the id
     input never runs, the submit listener never attaches, and Shop Pay
     stays greyed because Shopify's <shopify-accelerated-checkout> sees
     the disabled id input.

     Sold-out variants need to keep that disabled attribute (the Liquid
     ships it for exactly that reason), so we do NOT strip it. Instead
     we restore the lost <form> wrapper:

     1. Watch for <product-form> elements that arrive without a <form>
        child (only meaningful inside a <quick-shop>; the real PDP's
        product-form on /products/<handle> is parsed at top-level where
        there is no surrounding form and won't trip this case).
     2. Build a fresh <form> element with the right action / method /
        accept-charset / enctype / id that Liquid would have used.
     3. Move all the <product-form>'s current children into the new form,
        then attach the form back inside <product-form>.
     4. Fire <product-form>.connectedCallback() again so its init runs
        against the now-present this.form — sold-out variants keep their
        `disabled`, available variants get it stripped, the submit
        listener attaches, Shop Pay can hydrate.

     This is structural — it fixes the cause (missing <form>) instead of
     papering over the disabled attribute. Idempotent: if a <product-form>
     already has a <form> child, we leave it alone. */
  function ensureProductFormHasForm(pfEl) {
    if (!pfEl || pfEl.nodeType !== 1) return;
    if (!pfEl.matches || !pfEl.matches('product-form')) return;
    if (!pfEl.closest('quick-shop')) return;
    if (pfEl.querySelector(':scope > form')) return;

    /* Derive the form id and action that Liquid would have used. The
       quantity input carries `form="product-form-<uniqueId>"` — that's
       the id Liquid intended for the form. */
    var formId = null;
    var refInput = pfEl.querySelector('input[form^="product-form-"]') ||
                   document.querySelector('input[form^="product-form-"]');
    if (refInput) formId = refInput.getAttribute('form');
    if (!formId) {
      /* Fallback: use the variant component's uniqueId. */
      var qs = pfEl.closest('quick-shop');
      var vmi = qs && qs.querySelector('variant-mixed-inputs, variant-selects, variant-radios');
      var uniqueId = vmi && (vmi.uniqueId || vmi.dataset.card || vmi.dataset.section);
      if (uniqueId) formId = 'product-form-' + uniqueId;
    }

    var form = document.createElement('form');
    form.method = 'post';
    form.action = '/cart/add';
    form.enctype = 'multipart/form-data';
    form.setAttribute('accept-charset', 'UTF-8');
    form.setAttribute('novalidate', 'novalidate');
    if (formId) form.id = formId;
    form.className = 'product-form__form';

    /* Move all existing children into the form (preserving order). */
    while (pfEl.firstChild) form.appendChild(pfEl.firstChild);
    pfEl.appendChild(form);

    /* Re-run the product-form connectedCallback so its init picks up the
       newly-present this.form. This safely runs the
       `idInput.removeAttribute('disabled')` step ONLY for the available
       variant (Liquid renders disabled on the id input only when the
       form is for an available variant; sold-out variants render a
       button[disabled] instead, which we never touch). */
    if (typeof pfEl.connectedCallback === 'function') {
      try { pfEl.connectedCallback(); } catch (e) {}
    }
  }
  function sweepProductForms() {
    document.querySelectorAll('quick-shop product-form').forEach(ensureProductFormHasForm);
  }
  var formRestoreObserver = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      for (var j = 0; j < m.addedNodes.length; j++) {
        var n = m.addedNodes[j];
        if (!n || n.nodeType !== 1) continue;
        if (n.matches && n.matches('product-form')) ensureProductFormHasForm(n);
        if (n.querySelectorAll) {
          n.querySelectorAll('product-form').forEach(ensureProductFormHasForm);
        }
      }
    }
  });
  formRestoreObserver.observe(document.body, { childList: true, subtree: true });
  /* Initial sweep — covers anything already on-page. */
  sweepProductForms();

  /* theme.min.js is loaded with defer; this file is also defer + ordered after
     it, so customElements.get('variant-selects') should resolve on first try.
     Belt-and-suspenders: poll briefly in case some other deferred script
     reorders execution. */
  if (install()) return;
  var attempts = 0;
  var iv = setInterval(function () {
    attempts++;
    if (install() || attempts > 100) clearInterval(iv);
  }, 50);
})();
